/**
 * Debounce utility to limit the frequency of function calls
 * Prevents rapid successive API requests from degrading performance
 */

type DebouncedFunction<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): void;
  cancel: () => void;
};

/**
 * Creates a debounced version of the provided function
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @returns Debounced function with cancel method
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): DebouncedFunction<T> {
  let timeoutId: NodeJS.Timeout | undefined;

  const debouncedFunction = (...args: Parameters<T>) => {
    const callNow = !timeoutId;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = undefined;
      func(...args);
    }, wait);
  };

  debouncedFunction.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  return debouncedFunction;
}

/**
 * Throttle utility to limit function calls to once per specified time period
 * @param func The function to throttle
 * @param limit The number of milliseconds to wait between calls
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): T {
  let inThrottle: boolean;
  
  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }) as T;
}

/**
 * Request deduplication utility to prevent duplicate API calls
 * Useful for preventing multiple simultaneous requests to the same endpoint
 */
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<any>>();

  /**
   * Deduplicates requests by key
   * @param key Unique identifier for the request
   * @param requestFn Function that returns a Promise
   * @returns The promise (either existing or new)
   */
  deduplicate<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    const promise = requestFn()
      .finally(() => {
        // Clean up after request completes
        this.pendingRequests.delete(key);
      });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Clear all pending requests
   */
  clear() {
    this.pendingRequests.clear();
  }
}

// Global instance for API request deduplication
export const apiRequestDeduplicator = new RequestDeduplicator();